import { useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, X } from 'lucide-react';
import {
  Button,
  Description,
  Drawer,
  ListBox,
  type OverlayTriggerState,
} from '@heroui/react';
import { createMisuseReport } from '../../../api/misuse-reports';
import {
  formatLicensePlateInput,
  isValidBrazilianLicensePlate,
  normalizeLicensePlate,
} from '../../../utils/license-plate';
import { AsyncButton } from '../../../components/ui/async-button';
import {
  DrawerSelectField,
  DrawerTextAreaField,
  DrawerTextField,
} from '../../../components/ui/drawer-form-field';
import {
  drawerBodyClassName,
  drawerFooterClassName,
  sideDrawerDialogClassName,
} from '../../../components/ui/side-drawer-classes';
import { ImageFilePicker } from '../../../components/ui/image-file-picker';
import { StatusCallout } from '../../../components/ui/status-callout';
import { uploadMisuseReportPhoto } from '../../../lib/firebase-storage';
import { getErrorMessage } from '../../../lib/toast';

export interface ReportMisuseSpotOption {
  id: string;
  code: string;
}

interface ReportMisuseDrawerProps {
  state: OverlayTriggerState;
  spots: ReportMisuseSpotOption[];
  onSubmitted?: (notifiedCount: number) => void;
}

export function ReportMisuseDrawer({
  state,
  spots,
  onSubmitted,
}: ReportMisuseDrawerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [spotId, setSpotId] = useState<string | null>(null);
  const [licensePlate, setLicensePlate] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedSpot = spots.find((spot) => spot.id === spotId) ?? null;
  const defaultSpotId = spots.length === 1 ? spots[0].id : null;
  const headingCode = selectedSpot?.code ?? (defaultSpotId ? spots[0].code : null);

  useEffect(() => {
    if (!state.isOpen) return;
    setSpotId(defaultSpotId);
    setLicensePlate('');
    setNotes('');
    setPhotoFile(null);
    setPhotoPreview(null);
    setError(null);
    setSubmitting(false);
  }, [state.isOpen, defaultSpotId]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoChange = (file: File | null) => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(file ? URL.createObjectURL(file) : null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!spotId) {
      setError('Selecione a vaga para reportar.');
      return;
    }
    if (!isValidBrazilianLicensePlate(licensePlate)) {
      setError('Informe uma placa válida (ABC-1D23 ou ABC-1234).');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      let photoUrl: string | undefined;
      if (photoFile) {
        try {
          photoUrl = await uploadMisuseReportPhoto(photoFile);
        } catch (uploadError) {
          handlePhotoChange(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          setError(
            `${getErrorMessage(uploadError, 'Não foi possível enviar a foto.')} Toque em Notificar moradores de novo para enviar só com a placa.`,
          );
          setSubmitting(false);
          return;
        }
      }

      const result = await createMisuseReport({
        spotId,
        licensePlate: normalizeLicensePlate(licensePlate),
        photoUrl,
        notes: notes.trim() || undefined,
      });
      onSubmitted?.(result.notifiedCount);
      state.close();
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível enviar o relato.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>
                {headingCode ? `Reportar mau uso · ${headingCode}` : 'Reportar mau uso de vaga'}
              </Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>

            <Drawer.Body className={drawerBodyClassName}>
              <Description>
                Avise os moradores do condomínio que um carro está estacionado indevidamente na sua
                vaga. A placa é obrigatória; a foto ajuda a identificar.
              </Description>

              <StatusCallout status="warning" title="Todos os moradores serão notificados">
                O relato aparece nas notificações do condomínio, com a placa e a foto se você
                enviar.
              </StatusCallout>

              {error ? <StatusCallout status="danger">{error}</StatusCallout> : null}

              {spots.length > 1 ? (
                <DrawerSelectField
                  label="Sua vaga"
                  value={spotId}
                  onChange={setSpotId}
                  placeholder="Selecione a vaga"
                  isDisabled={submitting}
                  isRequired
                >
                  <ListBox>
                    {spots.map((spot) => (
                      <ListBox.Item key={spot.id} id={spot.id} textValue={spot.code}>
                        {spot.code}
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </DrawerSelectField>
              ) : spots.length === 1 ? (
                <Description>
                  Vaga <span className="font-mono font-medium">{spots[0].code}</span>
                </Description>
              ) : (
                <StatusCallout status="danger">Você não tem vaga neste condomínio.</StatusCallout>
              )}

              <DrawerTextField
                label="Placa do carro"
                value={licensePlate}
                onChange={(value) => setLicensePlate(formatLicensePlateInput(value))}
                placeholder="ABC-1D23"
                isDisabled={submitting}
              />

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Foto (opcional)</span>
                {photoPreview ? (
                  <div className="relative overflow-hidden rounded-xl border border-separator">
                    <img
                      src={photoPreview}
                      alt="Prévia da foto do mau uso"
                      className="max-h-48 w-full object-cover"
                    />
                    <Button
                      type="button"
                      isIconOnly
                      size="sm"
                      variant="secondary"
                      className="absolute right-2 top-2"
                      isDisabled={submitting}
                      onPress={() => {
                        handlePhotoChange(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <ImageFilePicker
                    inputRef={fileInputRef}
                    disabled={submitting}
                    aria-label="Adicionar foto"
                    onFile={(file) => handlePhotoChange(file)}
                  >
                    <Button type="button" variant="secondary" isDisabled={submitting} tabIndex={-1}>
                      <Camera size={16} />
                      Adicionar foto
                    </Button>
                  </ImageFilePicker>
                )}
                <Description>Use a câmera ou uma imagem da galeria, até 5 MB.</Description>
              </div>

              <DrawerTextAreaField
                label="Observação (opcional)"
                value={notes}
                onChange={setNotes}
                placeholder="Ex.: carro branco parado desde o início da manhã."
                isDisabled={submitting}
              />
            </Drawer.Body>

            <Drawer.Footer className={drawerFooterClassName}>
              <Button type="button" variant="secondary" isDisabled={submitting} onPress={() => state.close()}>
                Cancelar
              </Button>
              <AsyncButton
                variant="danger"
                isPending={submitting}
                pendingLabel="Enviando"
                isDisabled={!spotId}
                onPress={handleSubmit}
              >
                <ImagePlus size={16} />
                Notificar moradores
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
